import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategoryDropdown() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section id="categories" className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-3 whitespace-nowrap">
            Browse Categories
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
            Explore <span className="text-primary-500">Sugargoo Spreadsheet</span> Categories
          </h2>
          <p className="text-sm text-foreground-500 mt-3 max-w-xl mx-auto">
            Select a category below to browse curated product collections on our main website. Each category contains hand-picked finds from the Sugargoo community.
          </p>
        </div>

        <div className="flex justify-center" ref={dropdownRef}>
          <div className="relative w-full max-w-lg">
            <button
              onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50 border-2 border-background-200 rounded-xl text-sm font-medium text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-menu-search-line text-primary-500 text-lg"></i>
                {open ? 'Choose a Category...' : 'Select a Category to Browse...'}
              </span>
              {open ? (
                <i className="ri-arrow-up-s-line text-foreground-400 transition-transform duration-200"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-400 transition-transform duration-200"></i>
              )}
            </button>

            {open && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 border border-background-200/70 rounded-xl shadow-lg overflow-hidden z-40 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 gap-0">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-4 px-5 py-3.5 hover:bg-background-100 transition-colors cursor-pointer group border-b border-background-100 last:border-0"
                    >
                      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap block">
                          {cat.name}
                        </span>
                        <span className="text-xs text-foreground-400 mt-0.5 block">
                          Browse curated collection
                        </span>
                      </div>
                      <div className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center group-hover:bg-primary-100 transition-colors flex-shrink-0">
                        <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-500 text-sm transition-colors"></i>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}